import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLine, EASE } from "./Reveal";
import { BRAND } from "../../data/collection";

const SLIDES = [
  {
    id: "bloom",
    kicker: "Bloom Everyday",
    kickerAccent: "Bloom",
    kickerRest: "Everyday · Softly hand-embroidered",
    subtitle:
      "Hand-embroidered cotton to slip into any ordinary day — and let it bloom.",
    verse: "light & uncomplicated",
    image: "/collection/nora-1.jpg",
    ctaLabel: "Explore Bloom Everyday",
  },
  {
    id: "onam",
    kicker: "Onam Collection",
    kickerAccent: "Onam",
    kickerRest: "Collection · Handwoven",
    subtitle:
      "Slow-made pieces for the festival of harvest — woven by hand, cut to your measure.",
    verse: "woven for the harvest",
    image: "/collection/thumbapoo-1.jpg",
    ctaLabel: "Explore the Onam Collection",
  },
  {
    id: "everyday",
    kicker: "Everyday Collection",
    kickerAccent: "Everyday",
    kickerRest: "Collection · Made to be worn",
    subtitle:
      "Cotton dresses for the days you slip into softness — hand-embroidered, cut to your measure.",
    verse: "soft, slow, everyday",
    image: "/collection/iris-3.jpg",
    ctaLabel: "Explore the Everyday Collection",
  },
];

const AUTOPLAY_MS = 6500;

export default function Hero() {
  const ref = useRef(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const slide = SLIDES[i];

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((v) => (v + 1) % SLIDES.length), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused]);

  const goSlide = (idx) => {
    setI(idx);
    setPaused(true);
    // brief autoplay freeze so user's tap always wins over the current tick
    window.clearTimeout(goSlide._t);
    goSlide._t = window.setTimeout(() => setPaused(false), 2200);
  };

  const openCollection = () => {
    window.dispatchEvent(new CustomEvent("keilani:setCategory", { detail: slide.id }));
    const el = document.getElementById("collection");
    if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -20 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  // Basic touch swipe (mobile)
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 50) return;
    setI((v) => (dx < 0 ? (v + 1) % SLIDES.length : (v - 1 + SLIDES.length) % SLIDES.length));
  };

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="kln-noise" />

      {/* Parallax hero image — cross-fades between slides */}
      <motion.div style={{ y: imgY }} className="absolute right-0 top-0 h-full w-full md:w-[46vw]">
        <div className="relative h-full w-full overflow-hidden">
          <AnimatePresence mode="sync">
            <motion.img
              key={slide.id}
              style={{ scale: imgScale }}
              src={slide.image}
              alt={`Keilani ${slide.kicker}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.1, ease: EASE }}
              className="absolute inset-0 h-full w-full object-cover object-[center_20%] md:object-top grayscale-[12%]"
              data-testid={`hero-slide-${slide.id}`}
            />
          </AnimatePresence>
          {/* editorial italic verse overlay */}
          <AnimatePresence mode="wait">
            <motion.p
              key={`verse-${slide.id}`}
              initial={{ opacity: 0, y: 14, letterSpacing: "0.14em" }}
              animate={{ opacity: 1, y: 0, letterSpacing: "0.06em" }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.35 }}
              data-testid="hero-verse"
              className="pointer-events-none absolute bottom-[28%] right-4 md:right-10 max-w-[86%] md:max-w-[26vw] text-right font-serif-editorial italic text-white/85 text-[15px] sm:text-lg md:text-2xl drop-shadow-[0_2px_18px_rgba(0,0,0,0.6)]"
            >
              {slide.verse}
            </motion.p>
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-[#050505]/50 md:via-[#050505]/30 md:to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 to-[#050505]/20 md:from-[#050505]/70 md:to-transparent" />
        </div>
      </motion.div>

      {/* Copy */}
      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 flex min-h-[100svh] flex-col justify-center px-6 md:px-12 lg:px-20"
      >
        {/* animated kicker */}
        <AnimatePresence mode="wait">
          <motion.p
            key={`kicker-${slide.id}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.6, ease: EASE }}
            data-testid="hero-kicker"
            className="mb-6 font-body text-[11px] uppercase tracking-[0.4em] text-white/50"
          >
            <span className="text-[var(--kln-gold)]">{slide.kickerAccent}</span>{" "}
            {slide.kickerRest}
          </motion.p>
        </AnimatePresence>

        <h1 className="font-display font-light leading-[0.86] text-white">
          <MaskLine delay={0.15} className="text-[19vw] md:text-[13vw] lg:text-[11.5vw] tracking-[0.04em]">
            KEILANI
          </MaskLine>
          <span className="mt-1 flex items-end gap-4 md:gap-8">
            <MaskLine delay={0.32} className="text-[10.5vw] md:text-[7vw] lg:text-[6vw] tracking-[0.32em] text-white/70">
              CLOTHING
            </MaskLine>
          </span>
        </h1>

        {/* animated subtitle */}
        <div className="mt-8 max-w-md overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={`sub-${slide.id}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.7, ease: EASE }}
              data-testid="hero-subtitle"
              className="font-serif-editorial text-base sm:text-xl md:text-2xl font-light italic text-white/85 md:text-white/75"
            >
              {slide.subtitle}
            </motion.p>
          </AnimatePresence>
        </div>

        <motion.button
          data-testid="hero-scroll-cue"
          onClick={openCollection}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="group mt-12 flex w-fit items-center gap-3 font-body text-[11px] uppercase tracking-[0.3em] text-white/70 transition-colors duration-300 hover:text-white"
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={`cta-${slide.id}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.4 }}
              className="relative"
            >
              {slide.ctaLabel}
              <span className="absolute left-0 -bottom-1 h-px w-full origin-left scale-x-0 bg-white transition-transform duration-500 group-hover:scale-x-100" />
            </motion.span>
          </AnimatePresence>
          <ArrowDownRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1" strokeWidth={1.2} />
        </motion.button>

        {/* Slide dots */}
        <div
          data-testid="hero-dots"
          className="mt-10 flex items-center gap-3"
          role="tablist"
          aria-label="Collection slides"
        >
          {SLIDES.map((s, idx) => {
            const active = i === idx;
            return (
              <button
                key={s.id}
                data-testid={`hero-dot-${s.id}`}
                role="tab"
                aria-selected={active}
                aria-label={s.kicker}
                onClick={() => goSlide(idx)}
                className="group flex items-center gap-2"
              >
                <span
                  className={`relative h-[2px] transition-all duration-500 ${
                    active ? "w-10 bg-white" : "w-5 bg-white/25 group-hover:bg-white/60"
                  }`}
                >
                  {active && !paused && (
                    <motion.span
                      key={`bar-${idx}-${paused}`}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
                      className="absolute inset-0 origin-left bg-[var(--kln-gold)]"
                    />
                  )}
                </span>
                <span
                  className={`font-body text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.24em] transition-colors duration-500 ${
                    active ? "text-white" : "text-white/40 group-hover:text-white/70"
                  }`}
                >
                  <span className="sm:hidden">{s.kickerAccent}</span>
                  <span className="hidden sm:inline">{s.kicker}</span>
                </span>
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Bottom hairline meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="absolute bottom-6 left-6 right-6 z-10 hidden items-center justify-between border-t border-white/10 pt-4 font-body text-[10px] uppercase tracking-[0.3em] text-white/40 md:flex md:left-12 md:right-12"
      >
        <span>Est. Kerala · Made Slow</span>
        <span>{BRAND.instagramHandle}</span>
        <span>Custom Fit · XS — 4XL</span>
      </motion.div>
    </section>
  );
}
